import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { ScoreHistoryEntry, ScoreSummaryByType } from "../types";

interface UseScoreHistoryOptions {
  teamId?: string;
  userId?: string;
  limit?: number;
}

/**
 * Hook to fetch score history for a team or user
 */
export function useScoreHistory(options: UseScoreHistoryOptions = {}) {
  const { activeWeekend } = useAuth();
  const { teamId, userId, limit = 50 } = options;

  const [history, setHistory] = useState<ScoreHistoryEntry[]>([]);
  const [summaryByType, setSummaryByType] = useState<ScoreSummaryByType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch score history
   */
  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from("score_history")
        .select(`
          id,
          team_id,
          points,
          previous_total,
          new_total,
          reason,
          game_type,
          game_id,
          awarded_by,
          awarded_to,
          created_at,
          teams:team_id (
            name,
            color
          ),
          awarded_by_profile:awarded_by (
            full_name
          ),
          awarded_to_profile:awarded_to (
            full_name,
            avatar_url
          )
        `)
        .order("created_at", { ascending: false })
        .limit(limit);

      // Filter by team if provided
      if (teamId) {
        query = query.eq("team_id", teamId);
      }

      // Filter by user if provided
      if (userId) {
        query = query.eq("awarded_to", userId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Map the data to our type
      const mappedHistory: ScoreHistoryEntry[] = (data || []).map((entry: any) => ({
        id: entry.id,
        team_id: entry.team_id,
        team_name: entry.teams?.name,
        team_color: entry.teams?.color,
        points: entry.points,
        previous_total: entry.previous_total,
        new_total: entry.new_total,
        reason: entry.reason,
        game_type: entry.game_type,
        game_id: entry.game_id,
        awarded_by: entry.awarded_by,
        awarded_by_name: entry.awarded_by_profile?.full_name,
        awarded_to: entry.awarded_to,
        awarded_to_name: entry.awarded_to_profile?.full_name,
        awarded_to_avatar: entry.awarded_to_profile?.avatar_url,
        created_at: entry.created_at,
      }));

      setHistory(mappedHistory);

      // Calculate summary by game type
      const typeMap: Record<string, { total: number; count: number }> = {};
      mappedHistory.forEach((entry) => {
        const type = entry.game_type || "Overig";
        if (!typeMap[type]) {
          typeMap[type] = { total: 0, count: 0 };
        }
        typeMap[type].total += entry.points;
        typeMap[type].count += 1;
      });

      const summary: ScoreSummaryByType[] = Object.entries(typeMap)
        .map(([game_type, data]) => ({
          game_type,
          total_points: data.total,
          entries_count: data.count,
        }))
        .sort((a, b) => b.total_points - a.total_points);

      setSummaryByType(summary);
    } catch (err) {
      console.error("Error fetching score history:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch history");
    } finally {
      setLoading(false);
    }
  }, [teamId, userId, limit]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  /**
   * Calculate total points from history
   */
  const totalPoints = history.reduce((sum, entry) => sum + entry.points, 0);

  return {
    history,
    summaryByType,
    totalPoints,
    loading,
    error,
    refresh: fetchHistory,
  };
}

